import { apiOrderFood } from '@/api/OrderFood'
import { convertKeysToCamelCase } from '@/js/utils/bff'
import { getDisplaySpec, getCommunityTabletPointsData } from '@/js/utils/orderFood'
import i18n from '@/lang/i18n'

/** 社區點餐：僅櫃檯點餐（後台與 64 互斥，同時最多一項為 true） */
export const COMMUNITY_ORDER_FOOD_SERVICE_ID_COUNTER_ONLY = 63
/**
 * 社區點餐：櫃檯＋APP＋通知取餐（涵蓋範圍較 63 廣；住戶取餐入口、會館營業時間等依此開關）
 * 後台與 63 互斥，同時最多一項為 true
 */
export const COMMUNITY_ORDER_FOOD_SERVICE_ID_APP_PICKUP = 64

const state = {
  // 類別列表
  menuList: [],
  // 規格列表
  specList: [],
  /**
   * 點數類型列表
   * 1. 數值相應於餐點品項中 prices 的 priceType
   */
  pointTypeList: [],
  // 目前交易資料
  currentTransaction: {
    // 選擇的戶別資訊
    selectedHouseholdInfo: {
      current: {
        tabletId: '', // 戶別ID
        tabletNote: '', // 戶別名稱
        normalPoint: 0, // 一般點數
        cashPoint: 0 // 現金點數
      }
    },
    isPointsInsufficient: false, // 是否點數不足
    isPointSupplementEnabled: false, // 是否啟用點數補足功能
    isPointSupplementEnough: false // 是否以第二個點數類型足以補足
  },
  clubhouseBusinesses: [], // 會館營業時間
  clubhouseBeforeOpenHour: 0, // 會館營業前可預約時間
  clubhouseBeforeCloseHour: 0, // 會館營業結束前可預約時間
  clubhouseHolidays: [], // 會館休息日
  clubhouseTables: [], // 會館內用桌號
  onlineOrderRefreshTrigger: 0 // 推播觸發線上訂單刷新用，每次 +1
}

const getters = {
  /**
   * 取得餐點類別項目
   * @param {string} id 餐點類別id
   */
  getMenuItem: (state) => (id) => {
    return state.menuList.find(item => item.id === id)
  },
  /**
   * 取得餐點品項
   * @param {string} id 餐點品項id
   */
  getMenuFoodItem: (state) => (id) => {
    return state.menuList.flatMap(menu => menu.children).find(child => child.id === id)
  },
  /**
   * 取得餐點類別列表最新的排序值
   */
  getMenuListLatestSequence: (state) => {
    return state.menuList.length > 0 ? state.menuList[state.menuList.length - 1].sequence : 0
  },
  /**
   * 取得規格項目
   * @param {string} id 規格id
   */
  getSpecItem: (state) => (id) => {
    return state.specList.find(item => item.id === id)
  },
  /**
   * 取得規格列表最新的排序值
   */
  getSpecListLatestSequence: (state) => {
    return state.specList.length > 0 ? state.specList[state.specList.length - 1].sequence : 0
  },
  /**
   * 取得可用的規格列表
   * 1. 用於處理規格選擇
   */
  getAvailableSpecList: (state) => {
    return state.specList.filter(item => item.isCheck === 1)
      .map(({ id, name, sequence, children }) => {
        return {
          id,
          name,
          sequence,
          children: children.filter(child => child.isCheck === 1)
            .map(({ id, name, sequence }) => ({ id, name, sequence }))
        }
      })
  },
  /**
   * 取得點數類型標題
   * 1. 新點餐資料中的點數類型設計
   */
  getPointTypeTitle: (state) => (type) => {
    return state.pointTypeList.find(item => item.value === type)?.title || '-'
  },
  /**
   * 取得點數類型的id
   */
  getPointTypeId: (state) => (type) => {
    return state.pointTypeList.find(item => item.value === type)?.ptId || 0
  },
  /**
   * 是否為「僅櫃檯點餐」模式（isServiceEnabled 63）
   * 與 isCommunityOrderFoodAppPickupEnabled 互斥
   */
  isCommunityOrderFoodCounterOnlyEnabled: (state, getters, rootState, rootGetters) => {
    return rootGetters.isServiceEnabled(COMMUNITY_ORDER_FOOD_SERVICE_ID_COUNTER_ONLY)
  },
  /**
   * 是否為「櫃檯＋APP＋通知取餐」模式（isServiceEnabled 64）
   * 與 isCommunityOrderFoodCounterOnlyEnabled 互斥；住戶取餐、會館營業時間頁籤等依此顯示
   */
  isCommunityOrderFoodAppPickupEnabled: (state, getters, rootState, rootGetters) => {
    return rootGetters.isServiceEnabled(COMMUNITY_ORDER_FOOD_SERVICE_ID_APP_PICKUP)
  },
  /**
   * 取得會館營業時間列表
   */
  getClubhouseBusinesses: (state) => state.clubhouseBusinesses,
  /**
   * 取得會館營業前可預約時間
   */
  getClubhouseBeforeOpenHour: (state) => state.clubhouseBeforeOpenHour,
  /**
   * 取得會館營業結束前可預約時間
   */
  getClubhouseBeforeCloseHour: (state) => state.clubhouseBeforeCloseHour,
  /**
   * 取得會館休息日列表
   */
  getClubhouseHolidays: (state) => state.clubhouseHolidays,
  /**
   * 取得會館內用桌號列表
   */
  getClubhouseTables: (state) => state.clubhouseTables
}

const mutations = {
  // 設定點數類型列表
  setPointTypeList (state, data) {
    state.pointTypeList = []
    data.forEach(item => {
      state.pointTypeList.push({
        ...item,
        title: item.pointName,
        value: item.ptId === 0 ? 1 : 2
      })
    })
  },
  // 設定餐點類別列表
  setMenuList (state, data) {
    state.menuList = []
    data.forEach((menuType) => {
      menuType.isExpand = false
      menuType.children.forEach((menuItem) => {
        /**
         * 設定顯示規格
         * Ex => 甜度：正常，冰塊：少冰
         */
        menuItem.displaySpec = getDisplaySpec(menuItem.chooses, ', ')
      })
      state.menuList.push(menuType)
    })
  },
  // 更新餐點類別列表的排序
  updateMenuListOrder (state, { oldIndex, newIndex }) {
    const movedItem = state.menuList.splice(oldIndex, 1)[0]
    state.menuList.splice(newIndex, 0, movedItem)
  },
  // 更新餐點類別項目的展開狀態
  updateMenuItemExpand (state, { id, status }) {
    const targetItem = state.menuList.find(item => item.id === id)
    if (targetItem) { targetItem.isExpand = status }
  },
  // 設定規格列表
  setSpecList (state, data) {
    state.specList = []
    data.forEach((item) => {
      item.isExpand = false
      state.specList.push(item)
    })
  },
  // 更新規格列表的排序
  updateSpecListOrder (state, { oldIndex, newIndex }) {
    const movedItem = state.specList.splice(oldIndex, 1)[0]
    state.specList.splice(newIndex, 0, movedItem)
  },
  // 更新規格項目的展開狀態
  updateSpecItemExpand (state, { id, status }) {
    const targetItem = state.specList.find(item => item.id === id)
    if (targetItem) { targetItem.isExpand = status }
  },
  // 設定目前交易的戶別資訊
  setCurrentTransactionHouseholdInfo (state, { tabletId, tabletNote, normalPoint, cashPoint }) {
    state.currentTransaction.selectedHouseholdInfo.current.tabletId = tabletId
    state.currentTransaction.selectedHouseholdInfo.current.tabletNote = tabletNote
    state.currentTransaction.selectedHouseholdInfo.current.normalPoint = normalPoint
    state.currentTransaction.selectedHouseholdInfo.current.cashPoint = cashPoint
  },
  // 更新目前交易的戶別點數
  updateCurrentTransactionHouseholdInfo (state, { normalPoint, cashPoint }) {
    state.currentTransaction.selectedHouseholdInfo.current.normalPoint = normalPoint
    state.currentTransaction.selectedHouseholdInfo.current.cashPoint = cashPoint
  },
  // 設定目前交易的點數不足狀態
  setCurrentTransactionPointsInsufficient (state, status) {
    state.currentTransaction.isPointsInsufficient = status
  },
  // 設定是否啟用點數補足功能
  setCurrentTransactionIsPointSupplementEnabled (state, status) {
    state.currentTransaction.isPointSupplementEnabled = status
  },
  // 設定是否點數補足足夠
  setCurrentTransactionIsPointSupplementEnough (state, status) {
    state.currentTransaction.isPointSupplementEnough = status
  },
  // 設定會館資訊（營業時間、可預約時間、休息日、桌號）
  setClubhouseInfo (state, { businesses, holidays, tables, beforeOpenHour, beforeCloseHour }) {
    state.clubhouseBusinesses = businesses
    state.clubhouseBeforeOpenHour = beforeOpenHour
    state.clubhouseBeforeCloseHour = beforeCloseHour
    state.clubhouseHolidays = holidays
    state.clubhouseTables = tables
  },
  // 僅更新桌號列表
  setClubhouseTables (state, tables) {
    state.clubhouseTables = tables
  },
  // 觸發線上訂單刷新（由推播通知呼叫）
  triggerOnlineOrderRefresh (state) {
    state.onlineOrderRefreshTrigger += 1
  }
}

const actions = {
  getPointType ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getPointType()
        .then(res => {
          const { status, data } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            commit('setPointTypeList', data.pointTypes)
            resolve(true)
          } else {
            reject(new Error(i18n.t('OrderFood.GetPointTypeFailed')))
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 取得餐點分類
  getFoodMenu ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getFoodMenu()
        .then(res => {
          const { status, data, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            commit('setMenuList', data.tableFoodMenuSettings)
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 取得規格列表
  getFoodSpec ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getFoodSpec()
        .then(res => {
          const { status, data, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            commit('setSpecList', data.tableFoodSpecSettings)
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 更新戶別點數
  updateHouseholdPoints ({ state, commit }) {
    return new Promise((resolve, reject) => {
      getCommunityTabletPointsData(state.currentTransaction.selectedHouseholdInfo.current.tabletId)
        .then(res => {
          const { status, data } = res
          if (status === 1) {
            const normalPoint = data.pointTablets.find(item => item.ptId === 0)?.point // 一般點數
            const cashPoint = data.pointTablets.find(item => item.ptId !== 0)?.point // 現金點數
            commit('updateCurrentTransactionHouseholdInfo', { normalPoint, cashPoint })
            resolve(true)
          } else {
            reject(new Error(i18n.t('OrderFood.GetCommunityTabletPointsDataFailed')))
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 取得會館資訊（營業時間、休息日、桌號）
  fetchClubhouseInfo ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getClubhouseInfo()
        .then(res => {
          const { status, data, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            const businesses = data.businesses || []
            const holidays = data.holidays || []
            const tables = data.tables || []
            const beforeOpenHour = typeof data.reserveBeforeOpenHour === 'number' ? data.reserveBeforeOpenHour : 0
            const beforeCloseHour = typeof data.reserveBeforeCloseHour === 'number' ? data.reserveBeforeCloseHour : 0
            commit('setClubhouseInfo', { businesses, holidays, tables, beforeOpenHour, beforeCloseHour })
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 儲存會館營業時間與休息日
  saveClubhouseBusinessAndHoliday ({ dispatch }, payload) {
    return new Promise((resolve, reject) => {
      apiOrderFood.saveClubhouseBusinessAndHoliday(payload)
        .then(res => {
          const { status, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            // 儲存成功後重新取得最新資料
            dispatch('fetchClubhouseInfo')
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 新增內用桌號
  createClubhouseTableNo ({ dispatch }, payload) {
    return new Promise((resolve, reject) => {
      apiOrderFood.addClubhouseTableNo(payload)
        .then(res => {
          const { status, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            dispatch('fetchClubhouseInfo')
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 刪除內用桌號
  deleteClubhouseTableNo ({ dispatch }, id) {
    return new Promise((resolve, reject) => {
      apiOrderFood.deleteClubhouseTableNo(id)
        .then(res => {
          const { status, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            dispatch('fetchClubhouseInfo')
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  }
}

export default { state, getters, mutations, actions }
